/**
 * Thin typed client for the Better Auth endpoints moly-api serves under
 * `${AUTH_URL}/auth` (contract: moly-api/test/integration/auth.test.ts).
 *
 * Bearer tokens, never cookies: every request uses `credentials: 'omit'`, and
 * the session token comes back in the `set-auth-token` response header (moly-api
 * exposes it over CORS). Any response may carry a refreshed token there, so every
 * call returns it alongside the parsed body.
 *
 * Why not `better-auth/react`: the web app needs only these few calls, the
 * session token handling (header capture, IndexedDB mirroring, offline reads)
 * is ours anyway, and plain fetch keeps the token layer trivially mockable.
 */

import { toAuthUser } from './authStorage'
import type { AuthUser } from './authStorage'
import { AUTH_URL } from '@/lib/config'

export const AUTH_BASE_PATH = '/auth'
export const SESSION_TOKEN_HEADER = 'set-auth-token'

/**
 * An error from the auth server. `status` is 0 when the request never got a
 * response (offline, DNS, CORS, timeout). `code` is Better Auth's error code
 * (e.g. INVALID_EMAIL_OR_PASSWORD) when the body carried one.
 */
export class AuthApiError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(status: number, code: string | null, message: string) {
    super(message)
    this.name = 'AuthApiError'
    this.status = status
    this.code = code
  }

  get isNetworkError(): boolean {
    return this.status === 0
  }
}

interface AuthResponse<T> {
  data: T
  /** The session token from `set-auth-token`, when the server sent one. */
  sessionToken: string | null
}

interface RequestOptions {
  method: 'GET' | 'POST'
  body?: Record<string, unknown>
  sessionToken?: string | null
  signal?: AbortSignal
}

const parseBody = async (response: Response): Promise<unknown> => {
  try {
    const text = await response.text()
    return text ? (JSON.parse(text) as unknown) : null
  } catch {
    return null
  }
}

const readString = (value: unknown, key: string): string | null => {
  if (!value || typeof value !== 'object') return null
  const field = (value as Record<string, unknown>)[key]
  return typeof field === 'string' && field.length > 0 ? field : null
}

async function authRequest<T>(
  path: string,
  { method, body, sessionToken, signal }: RequestOptions,
  parse: (data: unknown) => T,
): Promise<AuthResponse<T>> {
  const headers = new Headers({ Accept: 'application/json' })
  if (body) headers.set('Content-Type', 'application/json')
  if (sessionToken) headers.set('Authorization', `Bearer ${sessionToken}`)

  let response: Response
  try {
    response = await fetch(`${AUTH_URL}${AUTH_BASE_PATH}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'omit',
      signal,
    })
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network request failed'
    throw new AuthApiError(0, null, message)
  }

  const data = await parseBody(response)
  if (!response.ok) {
    throw new AuthApiError(
      response.status,
      readString(data, 'code'),
      readString(data, 'message') ?? response.statusText,
    )
  }

  return {
    data: parse(data),
    sessionToken: response.headers.get(SESSION_TOKEN_HEADER) || null,
  }
}

const ignoreBody = (): void => undefined

const readUser = (data: unknown): AuthUser | null =>
  data && typeof data === 'object' ? toAuthUser((data as Record<string, unknown>).user) : null

export interface SignedInSession {
  sessionToken: string
  user: AuthUser | null
}

/**
 * Sign-in and verify-email both create a session. The header is the contract;
 * the body's `token` (same value) is only a fallback should a proxy strip it.
 */
const toSignedInSession = (response: AuthResponse<unknown>): SignedInSession => {
  const sessionToken = response.sessionToken ?? readString(response.data, 'token')
  if (!sessionToken) {
    throw new AuthApiError(500, 'MISSING_SESSION_TOKEN', 'The server did not return a session token')
  }
  return { sessionToken, user: readUser(response.data) }
}

const identity = (data: unknown): unknown => data

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** Create an account. A 6-digit code is emailed; there is no session until it's verified. */
export async function signUpEmail(input: { email: string; password: string; name: string }): Promise<void> {
  await authRequest('/sign-up/email', { method: 'POST', body: input }, ignoreBody)
}

/** Verify the emailed sign-up code. Signs the user in. */
export async function verifyEmailOtp(input: { email: string; otp: string }): Promise<SignedInSession> {
  return toSignedInSession(
    await authRequest('/email-otp/verify-email', { method: 'POST', body: input }, identity),
  )
}

export type OtpType = 'email-verification' | 'forget-password'

/** Send (or resend) a code: sign-up verification, or the start of a password reset. */
export async function sendVerificationOtp(input: { email: string; type: OtpType }): Promise<void> {
  await authRequest('/email-otp/send-verification-otp', { method: 'POST', body: input }, ignoreBody)
}

/** Set a new password with the emailed reset code. Does not sign in. */
export async function resetPasswordWithOtp(input: {
  email: string
  otp: string
  password: string
}): Promise<void> {
  await authRequest('/email-otp/reset-password', { method: 'POST', body: input }, ignoreBody)
}

export async function signInEmail(input: { email: string; password: string }): Promise<SignedInSession> {
  return toSignedInSession(await authRequest('/sign-in/email', { method: 'POST', body: input }, identity))
}

/** The session's user, or null when the server no longer knows the session. */
export async function getSession(
  sessionToken: string,
  signal?: AbortSignal,
): Promise<AuthResponse<AuthUser | null>> {
  return authRequest('/get-session', { method: 'GET', sessionToken, signal }, readUser)
}

/** Mint a 15-minute API JWT from the session. 401 = session revoked or expired. */
export async function fetchAccessToken(
  sessionToken: string,
  signal?: AbortSignal,
): Promise<AuthResponse<string>> {
  return authRequest('/token', { method: 'GET', sessionToken, signal }, (data) => {
    const token = readString(data, 'token')
    if (!token) throw new AuthApiError(500, 'MISSING_ACCESS_TOKEN', 'The server did not return a token')
    return token
  })
}

export const SOCIAL_PROVIDERS = ['google', 'apple'] as const
export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number]

/** The Google/Apple sign-in the server has credentials for (an empty list hides the buttons). */
export async function getSocialProviders(signal?: AbortSignal): Promise<Array<SocialProvider>> {
  const response = await authRequest('/social-providers', { method: 'GET', signal }, (data) => {
    const providers = data && typeof data === 'object' ? (data as Record<string, unknown>).providers : null
    return Array.isArray(providers) ? SOCIAL_PROVIDERS.filter((p) => providers.includes(p)) : []
  })
  return response.data
}

/** Where the sign-in popup starts: a top-level page on the auth origin (Better Auth's oauth-popup). */
export function socialPopupStartUrl(provider: SocialProvider, popupOrigin: string, popupNonce: string): string {
  const params = new URLSearchParams({ provider, popupOrigin, popupNonce })
  return `${AUTH_URL}${AUTH_BASE_PATH}/oauth-popup/start?${params.toString()}`
}

/** Revoke the session on the server. */
export async function signOutSession(sessionToken: string, signal?: AbortSignal): Promise<void> {
  await authRequest('/sign-out', { method: 'POST', body: {}, sessionToken, signal }, ignoreBody)
}

// ---------------------------------------------------------------------------
// MCP OAuth (Better Auth oauth-provider). The browser reaches /oauth2/authorize
// without our bearer session, so the provider sends it to /oauth/login or
// /oauth/consent with a signed query; those pages resume with the session token.
// ---------------------------------------------------------------------------

const readRedirectUrl = (data: unknown): string => {
  const url = readString(data, 'url')
  if (!url) throw new AuthApiError(500, 'MISSING_REDIRECT_URL', 'The server did not return a redirect URL')
  return url
}

/** Resume authorization for the signed-in user; returns where to go next (consent, or the client). */
export async function continueOAuthAuthorization(sessionToken: string, oauthQuery: string): Promise<string> {
  const { data } = await authRequest(
    '/oauth2/continue',
    { method: 'POST', body: { selected: true, oauth_query: oauthQuery }, sessionToken },
    readRedirectUrl,
  )
  return data
}

/** Approve or deny the client; returns the client's redirect URL (with a code, or an error). */
export async function submitOAuthConsent(sessionToken: string, oauthQuery: string, accept: boolean): Promise<string> {
  const { data } = await authRequest(
    '/oauth2/consent',
    { method: 'POST', body: { accept, oauth_query: oauthQuery }, sessionToken },
    readRedirectUrl,
  )
  return data
}

export interface OAuthPublicClient {
  clientId: string
  name: string | null
  uri: string | null
}

/** The client's self-declared name and site (dynamic registration: not verified). */
export async function getOAuthPublicClient(sessionToken: string, clientId: string): Promise<OAuthPublicClient> {
  const { data } = await authRequest(
    `/oauth2/public-client?${new URLSearchParams({ client_id: clientId }).toString()}`,
    { method: 'GET', sessionToken },
    (body) => ({ clientId, name: readString(body, 'client_name'), uri: readString(body, 'client_uri') }),
  )
  return data
}
